import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GenericModalService } from '../../shared/generic-modal/generic-modal.service';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services/current-user.service';
import { DeveloperErrorCategoryComponent } from '../developer-error-category/developer-error-category.component';
import { ExceptionCategory } from '../models/exception-category';
import { ExceptionType } from '../models/exception-type';
import { ExceptionTypeArea } from '../models/exception-type-area';
import { DeveloperService } from '../services/developer.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule, DxDropDownBoxModule } from 'devextreme-angular';
import { DxoEditingModule, DxoPopupModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, DxiButtonModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-developer-error-codes',
    templateUrl: './developer-error-codes.component.html',
    styleUrls: ['./developer-error-codes.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoPopupModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxoLookupModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, DxDropDownBoxModule, DxoPagingModule, DxoScrollingModule, DxoSelectionModule]
})
export class DeveloperErrorCodesComponent implements OnInit, AfterViewInit {
    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    allowShowLog = false;
    exceptionTypes: ExceptionType[] = [];
    exceptionCategories: ExceptionCategory[] = [];
    exceptionTypeAreas: ExceptionTypeArea[] = [
        { ExceptionTypeAreaId: 'Unknown', ExceptionTypeAreaName: 'Unknown' }, 
        { ExceptionTypeAreaId: 'Polling', ExceptionTypeAreaName: 'Polling' },
        { ExceptionTypeAreaId: 'Send', ExceptionTypeAreaName: 'Send' },
        { ExceptionTypeAreaId: 'Itinerary', ExceptionTypeAreaName: 'Itinerary' },
        { ExceptionTypeAreaId: 'Initialize', ExceptionTypeAreaName: 'Initialize' },
        { ExceptionTypeAreaId: 'Receive', ExceptionTypeAreaName: 'Receive' }
    ];
    dropDownOptions = { width: 500 };


    constructor(
        private developerService: DeveloperService,
        private loadingPanelService: LoadingPanelService,
        private genericModalService: GenericModalService,
        private modalService: NgbModal,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {
        this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
        this.allowDeletingRow = this.allowDeletingRow.bind(this);
    }

    @ViewChild('errorCodeGrid', { static: true }) errorCodeGrid: BizDataGridDirective;

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_ErrorCode_Create');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_ErrorCode_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_ErrorCode_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    onEditorPreparing(e) {
        if ((e.dataField === 'Description' || e.dataField === 'TechnicalDescription' || e.dataField === 'ExceptionCause' || e.dataField === 'ExceptionSolution') && e.parentType === 'dataRow') {
            e.editorName = "dxTextArea";
        }
    }

    allowUpdatingRow(options: { component: any; row: any; }): boolean {
        return this.allowUpdating && !(options?.row?.data?.AutoGenerated);
    }

    allowDeletingRow(options: { component: any; row: any; }): boolean {
        return this.allowDeleting && !(options?.row?.data?.AutoGenerated);
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.exceptionTypes = [];

            let exceptionCategories: ExceptionCategory[] = []
            let exceptionTypes: ExceptionType[] = [];
            await Promise.all([
                this.developerService.getExceptionCategories().then(result => { exceptionCategories = result; }),
                this.developerService.getExceptionTypes().then(result => { exceptionTypes = result; })
            ]);

            exceptionTypes.forEach(et => {
                et.ExceptionCategoryCode = exceptionCategories.filter(ec => ec.ExceptionCategoriesId == et.ExceptionCategoryId)[0].ExceptionCategoryCode;
            });

            this.exceptionCategories = exceptionCategories;
            // Ensure that these are set last
            this.exceptionTypes = exceptionTypes;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        e.data = {
            ExceptionTypesId: 0,
            ErrorCode: null,
            TechnicalDescription: null,
            ExceptionCategoryId: null,
            ExceptionCategoryCode: null,
            ExceptionCause: null,
            ExceptionSolution: null,
            Area: null,
            Component: null,
            AutoGenerated: false
        } as ExceptionType;
        e.promise = this.developerService.generateErrorCode().then(data => {
            e.data.ErrorCode = data;
        });
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as ExceptionType, item => this.saveExceptionType(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as ExceptionType, item => this.saveExceptionType(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as ExceptionType, item => this.removeExceptionType(item));
    };

    async saveExceptionType(exceptionType: ExceptionType): Promise<ExceptionType> {
        const newExceptionType = await this.developerService.saveExceptionType(exceptionType);
        this.toastService.showToast('Error code saved...', ToastType.success);
        return newExceptionType;
    }

    async removeExceptionType(exceptionType: ExceptionType): Promise<ExceptionType> {
        await this.developerService.deleteExceptionType(exceptionType.ExceptionTypesId);
        this.toastService.showToast('Error code deleted...', ToastType.success);
        return exceptionType;
    }

    // For use with grid in drop down template
    onSelectionChanged(selectedRowKeys, cellInfo, dropDownBoxComponent): void {
        cellInfo.setValue(selectedRowKeys[0]);
        if (selectedRowKeys.length > 0) {
            dropDownBoxComponent.close();
        }
    }

    async newCategory(): Promise<void> {
        try {
            const modalRef = this.modalService.open(DeveloperErrorCategoryComponent, { backdrop: 'static', size: 'lg', centered: true });
            const result = await modalRef.result;
            console.log(result);
            const exCategory: ExceptionCategory = {
                ExceptionCategoriesId: 0,
                ExceptionCategoryCode: '',
                ExceptionCategoryName: result.name,
                Description: result.description
            };

            try {
                await this.developerService.saveExceptionCategory(exCategory);
                this.toastService.showToast('Error category saved...', ToastType.success);
                await this.refresh();
            }
            catch (err) {
                console.error(err);
                this.toastService.showToast('Failed to save error category...', ToastType.error);
            }
        }
        catch (err) {
            // We get here if the dialog is dismissed (canceled)
        }
    }

    async generateNewErrorCode(): Promise<void> {
        var newErrorCode = await this.developerService.generateErrorCode();
        var html = '<pre>' + newErrorCode + '</pre>';
        this.genericModalService.messageDirect('Error code', html);
    }

    async createErrorCode(): Promise<void> {
        this.errorCodeGrid.instance.addRow();
    }
}
